// 붙어 있는 디버그 스위치 — 「URL 로 한 번 주면 그대로 남는다」.
//
// 왜 신설했나: 성능 A/B 스위치를 `?greedybudget=0` 같은 **쿼리로만** 읽고 있었는데,
// 게임 안에서 이동하면(텔레포트·재진입) 주소가 `/game` 으로 돌아가 **쿼리가 통째로 날아간다**.
// 즉 스위치를 켜고 한 발짝 걸으면 꺼진다 — A/B 를 아예 못 한다. 실제로 밟았다.
//
// 그래서 두 층으로 읽는다:
//   1. 주소에 있으면 → 그 값을 쓰고 **저장소에 굳힌다**(다음부터 주소 없이도 유지)
//   2. 없으면 → 저장소를 본다
//   3. 그것도 없으면 → 기본값
// 되돌리기는 반대 값을 한 번 주면 된다 — `?greedybudget=8` · `?greedy=1`.
//
// ⚠ **읽는 곳이 둘이면 반드시 어긋난다.** 원래 이 값들을 PlacedBlocks(쓰는 쪽)와
//    GameCanvas(트레이스에 싣는 쪽)가 **각자 읽고** 있었다. 쿼리만 볼 때는 둘이 같은 답을
//    냈지만 저장소가 끼는 순간 갈린다(한쪽이 굳히기 전에 다른 쪽이 읽으면 다른 값이 나온다).
//    그래서 판정을 이 파일 하나로 모은다 — 이 프로젝트가 `featureRegistry.hasHudIcon` 에서
//    이미 배운 것과 같은 규칙이다: **「이게 켜져 있나」는 한 함수가 답한다.**
//
// ⚠ **저장소가 막힌 브라우저**(사생활 모드·쿠키 차단)에서도 죽지 않는다. 그때는 굳히기가
//    조용히 실패하고 쿼리·기본값으로 산다 — 스위치 하나 때문에 게임이 안 뜨면 안 된다.

import { MINSPEC_BY_KEY, MINSPEC_SWITCHES, PREFIX, parseSwitch } from './minspecSwitches';

function readRaw(key) {
  let fromUrl = null;
  try { fromUrl = new URLSearchParams(window.location.search).get(key); } catch { /* SSR·잠긴 창 */ }
  if (fromUrl !== null) {
    try { window.localStorage.setItem(PREFIX + key, fromUrl); } catch { /* 저장 막힘 — 이번 판만 적용 */ }
    return fromUrl;
  }
  try { return window.localStorage.getItem(PREFIX + key); } catch { return null; }
}

/** 최소사양 표(`minspecSwitches.js`)에 있는 스위치 하나의 값 — 키·범위·기본값은 그 표가 정한다. */
function minspecFlag(key) {
  const sw = MINSPEC_BY_KEY[key];
  return parseSwitch(sw, readRaw(sw.key));
}

/** 켜짐/꺼짐 스위치. `0` 만 꺼짐으로 읽는다(기본값은 호출자가 정한다). */
export function stickyBool(key, dflt) {
  const raw = readRaw(key);
  if (raw === null) return dflt;
  return raw !== '0';
}

/** 목록에서 고르는 스위치. `allowed` 밖의 값은 기본값으로 떨어뜨린다.
 *  오타 하나로 게임이 안 뜨면 안 되므로 거르되, 거른 사실은 콘솔에 남긴다. */
export function stickyEnum(key, dflt, allowed) {
  const raw = readRaw(key);
  if (raw === null) return dflt;
  if (allowed.includes(raw)) return raw;
  console.warn('[debugFlags] ?' + key + '=' + raw + ' 는 모르는 값 — ' + dflt + ' 로 간다. '
    + '고를 수 있는 것: ' + allowed.join(' · '));
  return dflt;
}

/** 숫자 스위치. `0` 은 「제한 없음」(Infinity)으로 읽는다 — 예산류 스위치의 관례다. */
export function stickyBudget(key, dflt) {
  const raw = readRaw(key);
  if (raw === null) return dflt;
  if (raw === '0') return Infinity;
  const n = parseFloat(raw);
  return Number.isFinite(n) && n > 0 ? n : dflt;
}

// ── 실제 스위치들 — 여기가 유일한 판정 지점이다 ───────────────────────────────
/** `?greedy=0` — 블록 면 제거(greedy mesher) 자체를 끈다. */
export const GREEDY_ON = stickyBool('greedy', true);
/** `?greedybudget=0` — 굽기 프레임 예산을 푼다(= 옛 동작). 숫자를 주면 그 ms 가 예산. */
export const GREEDY_BUDGET_MS = stickyBudget('greedybudget', 8);
/**
 * `?objbudget=0` — 오브젝트 **최초** 조립(mergeSubs + 재질 패치)의 프레임 예산을 푼다.
 *
 * 블록의 굽기 예산(`greedybudget`)과 **일부러 갈라 둔다.** 둘은 미룰 때 화면에 남는 것이
 * 다르다 — 블록은 옛 지오가 그대로 서 있지만(면 몇 장 차이), 오브젝트는 **첫 조립**이라
 * 미루면 그 그룹이 한두 프레임 안 그려진다(팝인). 즉 되돌릴 이유가 서로 다르므로
 * 스위치도 따로여야 한다.
 */
/** `?lod=1` — 나무 지오메트리 LOD. 기본 꺼짐이라 여기만 반대로 읽는다. */
export const LOD_ON = (() => {
  const raw = readRaw('lod');
  return raw === null ? false : raw === '1';
})();
/** `?loderr=0` — 청크 LOD 단 고르기를 **옛 미터 밴드**(`impostorStepAt`)로 되돌린다.
 *  끄면 종전과 픽셀 하나 안 다르다 — A/B 와 롤백을 같은 빌드에서 하려고 둔다. */
export const LOD_ERR_ON = stickyBool('loderr', true);
/** `?lodpx=N` — 청크 LOD 오차 예산 τ(화면 픽셀)를 직접 준다. 안 주면 `viewDistance.LOD_ERR`
 *  가 슬라이더에서 유도한다. ⚠ `?lodpx=0` 은 관례대로 Infinity 라 **전 구간 최대 단**이 된다
 *  (오차 상한 없음 = 가장 굵게. 극단 A/B 용). */
export const LOD_ERR_PX = stickyBudget('lodpx', 0);
/** `?lodfade=0` — 상세↔임포스터 디더 크로스페이드를 끄고 즉시 갈아탄다(= 옛 팝).
 *  숫자를 주면 그 ms 가 페이드 길이. 0.35초는 걸음 속도에서 「녹는다」로 읽히는 하한이다. */
export const LOD_FADE_MS = minspecFlag('lodfade') ? stickyBudget('lodfadems', 350) : 0;
/** `?lodhaze=0` — 원경 임포스터 공기원근을 끈다. 숫자는 세기(0~1). */
export const LOD_HAZE = minspecFlag('lodhaze') ? stickyBudget('lodhazek', 0.35) : 0;
/** `?blkquiet=0` — 시공물 도시 영역의 수송 개정 «조용한 창»과 같은 내용 재수신 무시를 끈다
 *  (조각이 올 때마다 영역을 다시 굽는 경로). 값은 창 길이(ms). */
export const BLK_QUIET_MS = stickyBool('blkquiet', true) ? 700 : 0;
/** `?worldmemo=0` — RpgWorld 의 지형·강·하이트필드 자식을 memo 없이 부모와 같이 다시 그린다. */
export const WORLD_MEMO_ON = stickyBool('worldmemo', true);
/** `?lodprio=0` — 지형 LOD 굽기 우선순위를 다시 `LodRegion` prop 으로 넘긴다(걸으면 memo 가 깨지는 옛 경로). */
export const LOD_PRIO_REF_ON = stickyBool('lodprio', true);
/** `?lodlivetick=0` — 지형 LOD 영역 승격을 프레임당 1개(매 프레임 setLive)로 되돌린다.
 *  켜면 동기화 틱(0.4초)마다 바뀐 영역을 한 번에 올린다. 메싱 워커가 없으면 늘 옛 경로다. */
export const LOD_LIVE_TICK_ON = stickyBool('lodlivetick', true);
/** `?lodfree=0` — 업로드 끝난 지형 LOD 정점 배열을 JS 힙에 그대로 둔다(옛 경로). */
export const LOD_FREE_ON = stickyBool('lodfree', true);
/**
 * `?partr=1` — 파티션·도로를 받는 청크 반경(칸). 기본 3 = 7×7 = ±2,047m.
 *
 * 이 값은 **혼자 서 있지 않다.** 받은 파티션의 bbox 가 곧 지형 활성 타일의 재료라
 * (`tilesInRange` 가 bbox ∩ 카메라반경), 줄이면 받는 양만 주는 게 아니라 **칠해진 지면이
 * 끝나는 거리가 같이 준다.** 그래서 A/B 를 코드 수정 없이 돌 수 있어야 한다 —
 * 「지면이 어디서 끊기나」는 헤드리스로 못 보고 실기 눈으로만 갈린다.
 *
 * 실측 행 수(2026-08-17, DB 직접 질의):
 *   후암   R=1 파티션 582 / 도로 1,002 · R=2 1,372 / 2,654 · R=3 2,763 / 5,475
 *   최밀집 R=1 953 / 1,510 · R=2 2,065 / 4,153 · R=3 3,236 / 6,758
 * 즉 3→1 은 파티션 **−79%**(최밀집 −71%)이고, 타일당 전량 훑는 `localsForTile` 이
 * O(타일수 × 파티션수) 라 그 곱이 같이 준다.
 */
export const PARTITION_CHUNK_R_FLAG = minspecFlag('partr');   // null = 파일 기본값을 쓴다

/**
 * `?objfrustum=0` — 스캐터(나무·소품·건물 GLB) **인스턴스 프러스텀 컬링**을 끈다.
 *
 * 왜 스위치가 필요한가: 이 컬링은 「화면 밖이면 안 그린다」인데, 판정이 **8프레임마다**
 * 도는 압축 루프 위에 얹혀 있다. 즉 늙은 판정으로 자를 수 있다. 여유 14°·회전 강제 갱신
 * 10° 로 막아 뒀지만 **「가장자리에서 튀어나온다」는 실기 눈으로만 갈린다** — 그때
 * 배포 없이 되돌릴 문이 없으면 판정 자체가 안 난다(`?partr` 과 같은 이유).
 *
 * ⚠ 끄면 옛 동작(`frustumCulled={false}` + 거리 컷만)으로 정확히 돌아간다.
 */

/**
 * `?paintworker=0` — 지형 타일 **페인팅을 워커에서** 굽는다(켜짐이 기본).
 *
 * 2026-08-15 에 상수 하나로 꺼 뒀던 것을 되살린다. 껐던 근거는 실유저 텔레메트리의
 * **「대기열 0 · 발주 0 · 완성 0」** 이었는데, 그 뒤 계측이 갈라졌다 — 그 셋은 페인팅이
 * 아니라 **파티션 공급 사슬**의 칸이고(`hfStats.js` 「파티션 공급 사슬」 절), `발주 0` 은
 * 「플레이어 좌표를 아직 모른다」는 뜻이다. 그 사슬은 2026-08-16 에 동 → 512m 청크로
 * 통째로 재설계됐다(`chunkPartitions.js`). **끈 근거가 이미 죽어 있다.**
 *
 * 되살리는 값: 타일 1장 = 작업 캔버스 2080²×4 + 출력 2048²×2 + blur 2회이고
 * 실측 **473.7ms · 859.3ms** 가 전부 메인 스레드에서 나간다.
 *
 * ⚠ 이 스위치는 **아는 사람만 쓸 수 있다.** 실유저는 URL 을 모른다 → 코드 쪽에
 *   시간 기반 감시견을 같이 뒀다(`PAINT_DEADLINE_MS`, HeightfieldPoC).
 */
export const PAINT_WORKER_ON = stickyBool('paintworker', true);

// ── 최소사양 디버그 스위치 — 키·범위·기본값은 minspecSwitches.js 표. 편집 화면은 /admin/minspec ──
/** 렌더 픽셀비 상한. null 이면 설정·기기 천장 그대로. */
export const DPR_CAP = minspecFlag('dprcap');
/** 그림자맵(깊이 패스)을 강제로 끈다. `?shadows=1` 보다 이긴다. */
export const SHADOWMAP_ON = minspecFlag('shadowmap');
/** 후처리 합성기 전체(블룸·SMAA·N8AO·갓레이·비네트·그레이딩)를 끈다. */
export const POSTFX_ON = minspecFlag('postfx');
/** 강·하천 수면(WorldRivers)을 마운트하지 않는다. 물 칸 질의도 비어 물가가 마른 바닥이 된다. */
export const WATER_ON = minspecFlag('water');
/** 구름 지면 그림자를 끄고 하늘 구름 디테일을 0(낮음)으로 내린다. */
export const CLOUDS_ON = minspecFlag('clouds');
/** 분위기 이펙트(반딧불·나비·창불·석등·용마산 불꽃·잠실 달 공명·동굴 빛줄기)를 안 그린다. */
export const AMBIENT_FX_ON = minspecFlag('ambientfx');
/** 원경 지형(FarTerrain) 렌더만 끈다. 파티션 반경 판정은 LAYERS ▸ 원경 토글을 그대로 따른다. */
export const FAR_WORLD_ON = minspecFlag('farworld');
/** 가시거리 기준값(m) 상한. 게임 범위 클램프는 viewDistance.applyViewDistances 가 한다. */
export const VIEW_CAP_M = minspecFlag('viewcap');
/** 몬스터·다른 플레이어 가시거리 배율 덮어쓰기. 0 이면 안 그린다. */
export const MOB_VIEW_MUL = minspecFlag('mobview');
/** NPC 가시거리 배율 덮어쓰기. 0 이면 대화 상대도 안 보인다. */
export const NPC_VIEW_MUL = minspecFlag('npcview');
/** 이펙트·월드 이름표·지면 소품·3D 잔디 가시거리 배율 덮어쓰기. */
export const FX_VIEW_MUL = minspecFlag('fxview');
/** 게임 화면 구석에 「최소사양 디버그」 배지(FPS·드로콜·삼각형)를 띄운다. */
export const MINSPEC_BADGE_ON = minspecFlag('minspec');

export const DEBUG_FLAG_PREFIX = PREFIX;

/** 표의 모든 스위치 값 — 하나도 기본값에서 안 벗어났으면 null(일반 유저 로그를 늘리지 않는다). */
function minspecSnapshot() {
  const out = {};
  let touched = false;
  for (const sw of MINSPEC_SWITCHES) {
    const v = minspecFlag(sw.key);
    out[sw.key] = v;
    if (v !== sw.dflt) touched = true;
  }
  return touched ? out : null;
}
const MINSPEC_SNAPSHOT = minspecSnapshot();

/** 트레이스·로그에 실을 요약. 켠 판과 안 켠 판을 나중에 로그에서 갈라내는 유일한 근거다. */
export function debugFlagSummary() {
  return {
    lod: LOD_ON,
    pw: PAINT_WORKER_ON,
    gr: GREEDY_ON,
    gb: GREEDY_BUDGET_MS !== Infinity,
    gbms: GREEDY_BUDGET_MS === Infinity ? 0 : GREEDY_BUDGET_MS,
    // ★ 반드시 실어야 한다 — 이걸 빼면 A/B 를 돌려 놓고 **녹화에서 어느 팔이었는지 못 가른다**.
    //   스위치를 만들고 요약에 안 넣는 건 실험을 하고 라벨을 안 붙이는 것과 같다.
    pr: PARTITION_CHUNK_R_FLAG,   // null = 기본값(3)
    bq: BLK_QUIET_MS,
    wm: WORLD_MEMO_ON,
    // 최소사양 스위치 — 하나도 안 건드렸으면 null(일반 유저 로그를 늘리지 않는다)
    ms: MINSPEC_SNAPSHOT,
  };
}

// 기본값이 아닌 스위치가 하나라도 있으면 콘솔에 한 줄 남긴다 — 굳어 있는 스위치를 잊고
// 「왜 느리지/왜 빠르지」를 며칠 헤매는 걸 막는다(굳힌다는 건 잊힌다는 뜻이기도 하다).
try {
  const s = debugFlagSummary();
  if (s.lod || !s.gr || !s.gb || s.pr !== null || !s.of || !s.pw) {
    // eslint-disable-next-line no-console
    console.info(`[debugFlags] 굳어 있는 스위치 — lod=${s.lod} greedy=${s.gr} 예산=${s.gb ? `${s.gbms}ms` : '해제'} 파티션반경=${s.pr ?? '기본(3)'} 오브젝트프러스텀=${s.of} 페인팅워커=${s.pw}`);
  }
  if (s.ms) console.info('[debugFlags] 최소사양 스위치 굳어 있음 —', JSON.stringify(s.ms), '· 복원: /admin/minspec');
} catch { /* 콘솔이 없어도 산다 */ }

// ── URL 플래그 두 개, 여기로 이사 왔다 ──────────────────────────
// [왜] 이 둘은 각각 `blockStream.js` / `objectBaked.js` 안에 살았는데, `api/game.js` 가
//   **불리언 하나** 때문에 그 두 모듈을 정적 import 했다. 그 모듈들은 buildStore →
//   chunkField → blockAo → `import * as THREE from 'three'` 로 이어져 있어서,
//   **로그인 화면이 Three.js 2MB 를 통째로 받고 있었다.** 값 두 개를 옮기는 것만으로
//   진입 그래프에서 three 유입구가 사라진다(로직 변경 0).
//   원래 자리에서는 여기 것을 다시 export 하므로 기존 소비처는 한 줄도 안 바뀐다.

/** `?chunks=0` 이면 끔, `?chunks=1` 이면 켬, 없으면 localStorage → **기본 켬**. */
export const BLOCK_STREAM_ON = (() => {
  try {
    const q = new URLSearchParams(window.location.search).get('chunks');
    if (q === '1') return true;
    if (q === '0') return false;
    const ls = window.localStorage.getItem('block_stream');
    if (ls === '0') return false;   // 명시적으로 꺼 둔 사람은 존중한다
    return true;                     // 기본 ON (2026-08-15)
  } catch {
    // SSR·프라이빗 모드 등 — 못 읽으면 **끈** 채로 둔다. 여기서 켜면 스트리밍이 필요한
    // 요청 루프(tickBlockStream)가 도는데 URL 에 stream=chunks 가 안 실렸을 수도 있어
    // 「서버는 통짜를 보냈는데 클라는 청크를 기다리는」 어긋남이 난다. 안전한 쪽이 OFF 다.
    return false;
  }
})();

// ⚠ `BAKED_ON`(`?baked=1`) 은 **삭제됐다**. 정적 베이크 스캐터
//   (`/scatter_baked/scatter.bin`)는 `placed_objects` 를 파일로 미리 구운 것이었고,
//   그 표가 폐기·0행이 되면서 굽을 원본이 사라졌다. 서버의 `request_objects`·
//   `sync_object_delta` 핸들러도 같이 없어져 해시 합의가 성립할 수 없다.
